import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { Grid } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@totalsoft/rocket-ui';
import { PdfCanvasStyled, PageNumbersContainer, PDFPreviewContainer } from './DocumentsStyles';

export default function PDFPreviewComponent({ url, rotationAngle, viewportScale }) {
  const { t } = useTranslation();
  const canvasRef = useRef();
  const divRef = useRef();
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const [pdfRef, setPdfRef] = useState();
  const [currentPage, setCurrentPage] = useState(1);

  const renderPage = useCallback(
    pageNum => {
      pdfRef &&
        pdfRef.getPage(pageNum).then(function (page) {
          const viewport = page.getViewport({ scale: viewportScale });
          const canvas = canvasRef.current;
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const renderContext = {
            canvasContext: canvas.getContext('2d', { willReadFrequently: true }),
            viewport: viewport
          };
          page.render(renderContext);
        });
    },
    [pdfRef, viewportScale]
  );

  const renderAllPages = useCallback(
    page => {
      const viewport = page.getViewport({ scale: viewportScale });
      const wrapper = document.createElement('div');
      wrapper.style.marginBottom = '15px';
      const pageCanvas = document.createElement('canvas');
      pageCanvas.height = viewport.height;
      pageCanvas.width = viewport.width;
      const renderContext = {
        canvasContext: pageCanvas.getContext('2d', { willReadFrequently: true }),
        viewport: viewport
      };
      wrapper.appendChild(pageCanvas);
      divRef.current.appendChild(wrapper);

      page.render(renderContext);
    },
    [viewportScale]
  );

  const renderPages = useCallback(() => {
    for (let num = 1; num <= pdfRef?.numPages; num++) pdfRef.getPage(num).then(renderAllPages);
  }, [renderAllPages, pdfRef]);

  const handleBackPage = useCallback(() => {
    setCurrentPage(currentPage - 1);
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(currentPage + 1);
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === null) {
      renderPages();
    } else if (pdfRef?.numPages >= currentPage) {
      renderPage(currentPage, pdfRef);
    }
  }, [pdfRef, currentPage, renderPage, renderPages]);

  useEffect(() => {
    const loadingTask = pdfjsLib.getDocument(url);
    loadingTask.promise.then(
      loadedPdf => {
        setPdfRef(loadedPdf);
      },
      function (reason) {
        console.error(reason);
      }
    );
  }, [url]);

  return (
    <Grid container alignItems='center'>
      <Grid item xs={1}>
        <IconButton variant='text' onClick={handleBackPage} disabled={currentPage === 1}>
          <ArrowBack />
        </IconButton>
      </Grid>
      <Grid item xs={10}>
        <PDFPreviewContainer container>
          <div ref={divRef}>
            <PdfCanvasStyled rotationAngle={rotationAngle}>
              <canvas ref={canvasRef}></canvas>
            </PdfCanvasStyled>
          </div>
          <PageNumbersContainer>
            {t('OfferEdit.Documents.Preview.PageNumbers', { currentPage, totalPages: pdfRef?.numPages })}
          </PageNumbersContainer>
        </PDFPreviewContainer>
      </Grid>
      <Grid item xs={1} display='flex' alignItems='center' justifyContent='flex-end'>
        <IconButton variant='text' onClick={handleNextPage} disabled={currentPage === pdfRef?.numPages}>
          <ArrowForward />
        </IconButton>
      </Grid>
    </Grid>
  );
}

PDFPreviewComponent.propTypes = {
  url: PropTypes.string,
  rotationAngle: PropTypes.number.isRequired,
  viewportScale: PropTypes.number.isRequired
};
